/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * CART PROMOTION TIERS (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Powers <cart-promo-tiers> rendered by snippets/cart-promo-tiers.liquid (cart drawer + cart page).
 * All display logic and eligibility are computed in Liquid from Shopify's cart data; this script only:
 *   - adds the gift product to the cart (tagged with the hidden "_promo_gift" line property)
 *   - removes the gift line when the fresh cart no longer qualifies, and clamps it to quantity 1
 *   - animates tier progress between re-renders
 *
 * To avoid painting a stale cart (gift still present at full price), the theme's "cart:refresh" is intercepted
 * in the capture phase when the freshly rendered section shows the gift must change; the fix is applied and a
 * new "cart:refresh" is dispatched with the corrected cart and sections.
 */
(function () {
  const SECTION_ID = 'mini-cart';
  const GIFT_PROPERTY = '_promo_gift';
  const FAILED_FLAG = 'promoGiftFailed';
  const state = { lastProgress: null, lastTier: null, lastHeadline: null, busy: false };

  const routes = () => window.themeVariables.routes;
  const isCartPage = () => window.themeVariables.settings.pageType === 'cart';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  async function fetchCart() {
    const response = await fetch(`${routes().cartUrl}.js`, { credentials: 'same-origin' });
    return response.json();
  }

  async function changeLine(key, quantity) {
    const response = await fetch(`${routes().cartChangeUrl}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID })
    });

    if (!response.ok) {
      throw new Error(`Cart change failed (${response.status})`);
    }

    return response.json();
  }

  function dispatchCart(cart, extraDetail) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: Object.assign({ cart: cart }, extraDetail || {}) }));
  }

  function readState(element) {
    if (!element) {
      return null;
    }

    return {
      giftKey: element.dataset.giftLineKey || '',
      giftQuantity: parseInt(element.dataset.giftLineQuantity || '0', 10),
      eligible: element.dataset.giftEligible === 'true'
    };
  }

  // Returns the change the gift line needs (remove when not eligible, clamp to 1), or null.
  function giftFix(promoState) {
    if (!promoState || !promoState.giftKey) {
      return null;
    }

    if (!promoState.eligible) {
      return { key: promoState.giftKey, quantity: 0 };
    }

    if (promoState.giftQuantity > 1) {
      return { key: promoState.giftKey, quantity: 1 };
    }

    return null;
  }

  function findGiftLineElement() {
    const badge = document.querySelector(`#${SECTION_ID} .promo-gift-badge`);
    return badge ? badge.closest('line-item') : null;
  }

  function getFlag(name) {
    try { return sessionStorage.getItem(name); } catch (e) { return null; }
  }

  function setFlag(name, value) {
    try { value === null ? sessionStorage.removeItem(name) : sessionStorage.setItem(name, value); } catch (e) { /* storage unavailable */ }
  }

  // Intercept the theme's refresh when the fresh HTML shows the gift line must change.
  document.documentElement.addEventListener('cart:refresh', (event) => {
    const html = event.detail && event.detail.cart && event.detail.cart.sections && event.detail.cart.sections[SECTION_ID];

    if (!html || state.busy) {
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fix = giftFix(readState(doc.querySelector('cart-promo-tiers')));

    if (!fix) {
      return;
    }

    event.stopImmediatePropagation();
    state.busy = true;

    const passThrough = { openMiniCart: event.detail.openMiniCart, replacementDelay: fix.quantity === 0 ? 600 : event.detail.replacementDelay };
    const giftLineElement = findGiftLineElement();

    // Keep the user informed: reuse the theme's own line-item loader on the gift line while it is being fixed.
    giftLineElement && giftLineElement.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: fix.quantity } }));

    changeLine(fix.key, fix.quantity)
      .then((cart) => {
        state.busy = false;
        giftLineElement && giftLineElement.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: cart, newLineQuantity: fix.quantity } }));
        dispatchCart(cart, passThrough);
      })
      .catch(() => {
        state.busy = false;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: event.detail }));
      });
  }, true);

  document.documentElement.addEventListener('cart:updated', (event) => {
    const cart = event.detail && event.detail.cart;

    if (cart && cart.item_count === 0) {
      setFlag(FAILED_FLAG, null);
    }
  });

  class CartPromoTiers extends HTMLElement {
    connectedCallback() {
      this._applyFixIfNeeded();
      this._animate();

      if (getFlag(FAILED_FLAG)) {
        const giftPanel = this.querySelector('.promo-tiers__gift');
        giftPanel && (giftPanel.hidden = true);
      }

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action="add-gift"]');

        if (button) {
          event.preventDefault();
          this._addGift(button);
        }
      });
    }

    async _applyFixIfNeeded() {
      const fix = giftFix(readState(this));

      if (!fix || state.busy) {
        return;
      }

      state.busy = true;

      try {
        const cart = await changeLine(fix.key, fix.quantity);
        state.busy = false;
        isCartPage() ? window.location.reload() : dispatchCart(cart);
      } catch (e) {
        state.busy = false;
      }
    }

    _animate() {
      const target = parseFloat(this.dataset.progress) || 0;
      const tier = parseInt(this.dataset.tier || '0', 10);
      const headline = this.dataset.headline || '';
      const previousProgress = state.lastProgress;
      const previousTier = state.lastTier;
      const previousHeadline = state.lastHeadline;

      state.lastProgress = target;
      state.lastTier = tier;
      state.lastHeadline = headline;

      if (previousProgress === null || reducedMotion()) {
        return;
      }

      if (previousProgress !== target) {
        this.style.setProperty('--promo-progress', previousProgress);
        this.classList.add('is-animating');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.style.setProperty('--promo-progress', target));
        });
      }

      if (previousTier !== null && tier > previousTier) {
        const nodes = Array.from(this.querySelectorAll('.promo-tiers__node'));

        nodes.forEach((node, index) => {
          if (index + 1 > previousTier && node.classList.contains('is-reached')) {
            node.classList.remove('is-reached');
            node.classList.add('is-animating');

            requestAnimationFrame(() => {
              requestAnimationFrame(() => node.classList.add('is-reached'));
            });
          }
        });
      }

      if (previousHeadline !== headline) {
        const headlineElement = this.querySelector('.promo-tiers__headline');
        headlineElement && headlineElement.classList.add('is-changed');
      }

      const giftPanel = this.querySelector('.promo-tiers__gift');
      giftPanel && previousTier !== null && tier > previousTier && giftPanel.classList.add('is-entering');
    }

    async _addGift(button) {
      const variantId = parseInt(this.dataset.giftVariant || '0', 10);

      if (!variantId || state.busy) {
        return;
      }

      state.busy = true;
      button.setAttribute('disabled', '');
      button.setAttribute('aria-busy', 'true');

      try {
        const properties = {};
        properties[GIFT_PROPERTY] = '1';

        const response = await fetch(`${routes().cartAddUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties }], sections: SECTION_ID })
        });

        if (!response.ok) {
          throw new Error(`Gift add failed (${response.status})`);
        }

        const added = await response.json();
        let cart = await fetchCart();
        cart.sections = added.sections;

        // Safeguard: Shopify must have made the gift free. If not (discount inactive or not applicable
        // to this customer), remove it right away so nobody is charged for it.
        const giftLine = cart.items.find((item) => item.properties && item.properties[GIFT_PROPERTY]);

        if (giftLine && giftLine.final_line_price > 0) {
          cart = await changeLine(giftLine.key, 0);
          setFlag(FAILED_FLAG, '1');
        }

        state.busy = false;
        isCartPage() ? window.location.reload() : dispatchCart(cart);
      } catch (e) {
        state.busy = false;
        button.removeAttribute('disabled');
        button.removeAttribute('aria-busy');
      }
    }
  }

  if (!window.customElements.get('cart-promo-tiers')) {
    window.customElements.define('cart-promo-tiers', CartPromoTiers);
  }
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * OPEN THE CART DRAWER AFTER ADDING FROM A QUICK VIEW (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * With cart type "drawer", Focal opens the cart drawer after an add-to-cart, except when the product form lives
 * inside another drawer (so that adding from the cart's own recommendations does not re-open it). The desktop
 * quick view is such a drawer, so Focal leaves it open and shows an "item added / view cart" notice linking to the
 * cart page instead. The store wants the cart drawer everywhere and the cart page only as a fallback: close the
 * quick view and open the cart drawer once it has re-rendered.
 */
(function () {
  let openCartAfterRefresh = false;

  document.addEventListener('variant:added', (event) => {
    const quickBuy = event.target.closest && event.target.closest('quick-buy-drawer, quick-buy-popover');

    if (!quickBuy || window.themeVariables.settings.cartType !== 'drawer') {
      return;
    }

    openCartAfterRefresh = true;
    quickBuy.open = false;
  });

  document.documentElement.addEventListener('cart:refresh', (event) => {
    if (!openCartAfterRefresh || !event.detail) {
      return;
    }

    openCartAfterRefresh = false;
    event.detail.openMiniCart = true;
  }, true);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * TAP A PRODUCT IMAGE TO OPEN IT FULL SCREEN ON PHONES (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Focal only opens the product image zoom (the theme's PhotoSwipe modal, <product-image-zoom>) from the small
 * zoom icon over the image. On phones the store hides that icon (see the end of theme.css) and opens the same
 * modal when the shopper taps the image itself. Pinch-zoom, panning, the close button and the slide dots are
 * PhotoSwipe's own. Desktop keeps Focal's behaviour (click = previous/next slide, zoom icon = open the modal).
 *
 * The modal is opened through Focal's own zoom toggle button (hidden by CSS on phones, but still wired), so the
 * button's expanded state stays in sync; opening <product-image-zoom> directly makes the button re-open it and
 * creates a second PhotoSwipe instance that cannot be closed.
 *
 * Flickity calls preventDefault() on the click that ends a drag, so a swipe between slides never opens the modal.
 */
(function () {
  const isPhone = () => window.matchMedia(window.themeVariables.breakpoints.phone).matches;

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !isPhone()) {
      return;
    }

    const mediaItem = event.target.closest('.product__media-item[data-media-type="image"]');

    if (!mediaItem || event.target.closest('a, button')) {
      return;
    }

    const productMedia = mediaItem.closest('product-media');
    const imageZoom = productMedia && productMedia.querySelector('product-image-zoom');

    if (!imageZoom || imageZoom.open) {
      return;
    }

    event.preventDefault();

    const zoomButton = productMedia.querySelector(`.product__zoom-button[aria-controls="${imageZoom.id}"]`);

    if (zoomButton) {
      zoomButton.click();
    } else {
      imageZoom.open = true;
    }
  });
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * TAP TO ZOOM INSIDE THE PRODUCT IMAGE MODAL ON PHONES (store-specific)
 * ------------------------------------------------------------------------------------------------------------
 *
 * In Focal's PhotoSwipe modal a single finger only swipes between photos; zooming needs a pinch or a double tap,
 * which shoppers rarely discover. PhotoSwipe fires "pswpTap" for a single tap (never for a double tap, which it
 * reports separately). Focal only acts on mouse taps; on phones this zooms in to the theme's mobile zoom factor
 * at the finger, and a second tap zooms back out, mirroring Focal's own double-tap handler and reusing the same
 * PhotoSwipe zoom transition. Dragging a zoomed image pans it (PhotoSwipe default). Pinch and double tap still work.
 */
(function () {
  const isPhone = () => window.matchMedia(window.themeVariables.breakpoints.phone).matches;

  document.addEventListener('pswpTap', (event) => {
    if (!isPhone() || !event.detail || event.detail.pointerType === 'mouse') {
      return;
    }

    const image = event.target.closest && event.target.closest('.pswp__img');
    const imageZoom = image && image.closest('product-image-zoom');
    const photoSwipe = imageZoom && imageZoom.photoSwipeInstance;

    if (!photoSwipe || !photoSwipe.currItem) {
      return;
    }

    const initialZoomLevel = photoSwipe.currItem.initialZoomLevel;
    const maxZoomLevel = window.themeVariables.settings.mobileZoomFactor || 2;
    const point = event.detail.releasePoint;

    if (photoSwipe.getZoomLevel() !== initialZoomLevel) {
      photoSwipe.zoomTo(initialZoomLevel, point, 333);
    } else {
      photoSwipe.zoomTo(initialZoomLevel < 0.7 ? 1 : maxZoomLevel, point, 333);
    }
  });
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * 3 INSTALLMENTS LINE UNDER THE PRODUCT PRICE (store-specific, October 2026 A/B test)
 * ------------------------------------------------------------------------------------------------------------
 *
 * snippets/product-info.liquid renders "3 תשלומים של X ₪" right under the price list, with X = one third of the
 * price shown. Focal re-renders the price list on every variant change (and later code may change it too), so
 * the amount is derived from the price element itself: whenever the price list changes, the displayed sale price
 * is parsed back to cents, divided by three and formatted with the store's money format.
 */
(function () {
  function moneyFormat() {
    return (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '{{amount}} ₪';
  }

  // Same rules as Focal's formatMoney, for the placeholder the store's format uses
  function formatMoney(cents) {
    const format = moneyFormat();
    const placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const noDecimals = placeholder.indexOf('no_decimals') !== -1;
    const thousands = placeholder.indexOf('comma_separator') !== -1 ? '.' : placeholder.indexOf('space_separator') !== -1 ? ' ' : placeholder.indexOf('apostrophe_separator') !== -1 ? "'" : ',';
    const decimal = placeholder.indexOf('comma_separator') !== -1 ? ',' : '.';
    const parts = (cents / 100).toFixed(noDecimals ? 0 : 2).split('.');
    const amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  // Reads the displayed price back to cents using the same format
  function parseCents(text) {
    const placeholder = (moneyFormat().match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const decimal = placeholder.indexOf('comma_separator') !== -1 ? ',' : '.';
    const match = text.match(/\d[\d.,' \u00a0]*/);

    if (!match) {
      return null;
    }

    let digits = match[0].trim();
    const decimalIndex = digits.lastIndexOf(decimal);
    const hasDecimals = placeholder.indexOf('no_decimals') === -1 && decimalIndex !== -1 && digits.length - decimalIndex - 1 <= 2;
    const whole = (hasDecimals ? digits.slice(0, decimalIndex) : digits).replace(/\D/g, '');
    const fraction = hasDecimals ? (digits.slice(decimalIndex + 1).replace(/\D/g, '') + '00').slice(0, 2) : '00';

    return whole === '' ? null : parseInt(whole + fraction, 10);
  }

  function update(productMeta) {
    const line = productMeta.querySelector('[data-product-installments]');
    const amountElement = line && line.querySelector('[data-product-installments-amount]');
    const price = productMeta.querySelector('[data-product-price-list] .price:not(.price--compare)');

    if (!line || !amountElement) {
      return;
    }

    const visiblePrice = price ? price.cloneNode(true) : null;
    visiblePrice && visiblePrice.querySelectorAll('.visually-hidden').forEach((element) => element.remove());
    const cents = visiblePrice ? parseCents(visiblePrice.textContent) : null;

    if (cents === null) {
      line.hidden = true;
      return;
    }

    amountElement.textContent = formatMoney(Math.round(cents / 3));
    line.hidden = false;
  }

  function watch(productMeta) {
    const priceList = productMeta.querySelector('[data-product-price-list]');

    if (!priceList || priceList.hasAttribute('data-installments-watched')) {
      return;
    }

    priceList.setAttribute('data-installments-watched', '');
    new MutationObserver(() => update(productMeta)).observe(priceList, { childList: true, subtree: true, characterData: true });
    update(productMeta);
  }

  const start = () => document.querySelectorAll('product-meta').forEach(watch);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);

  // Shared with the single-or-pair selector below
  window.hcMoney = { formatMoney: formatMoney, parseCents: parseCents };
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * SINGLE-OR-PAIR SELECTOR ON EARRINGS (store-specific, October 2026 A/B test)
 * ------------------------------------------------------------------------------------------------------------
 *
 * snippets/product-form.liquid renders "עגיל אחד / זוג" option buttons after the variant picker on products of
 * type "עגילים". "זוג" is quantity 2 of the selected variant: the form's quantity is set to 2 and every displayed
 * price for that form (the product price list and the sticky form price) is shown doubled. Focal rewrites those
 * prices on every variant change, so each price element is re-read whenever its text is not the text we wrote
 * last, and the multiplier is applied to that fresh value. The installments line follows the displayed price.
 */
(function () {
  const multipliers = {};

  function visibleText(element) {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('.visually-hidden').forEach((hidden) => hidden.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  function setAmount(element, text) {
    // The amount is the last text node of the price element; the visually-hidden label before it stays untouched
    const textNodes = Array.from(element.childNodes).filter((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '');
    const node = textNodes[textNodes.length - 1];

    if (node) {
      node.textContent = text;
    } else {
      element.appendChild(document.createTextNode(text));
    }
  }

  function priceElements(formId) {
    return document.querySelectorAll(
      'product-meta[form-id="' + formId + '"] [data-product-price-list] .price, ' +
      'product-sticky-form[form-id="' + formId + '"] .product-sticky-form__price'
    );
  }

  function applyPrices(formId) {
    const multiplier = multipliers[formId] || 1;

    priceElements(formId).forEach((element) => {
      const current = visibleText(element);

      if (element.getAttribute('data-pair-applied') !== current) {
        const cents = window.hcMoney.parseCents(current);

        if (cents === null) {
          return;
        }

        element.setAttribute('data-pair-base', cents);
      }

      const base = parseInt(element.getAttribute('data-pair-base'), 10);
      const text = window.hcMoney.formatMoney(base * multiplier);

      if (visibleText(element) !== text) {
        setAmount(element, text);
      }

      element.setAttribute('data-pair-applied', visibleText(element));
    });
  }

  function applyQuantity(formId) {
    const form = document.getElementById(formId);
    const multiplier = multipliers[formId] || 1;
    let input = document.querySelector('[form="' + formId + '"][name="quantity"]') || (form && form.querySelector('[name="quantity"]'));

    if (!input && form) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.appendChild(input);
    }

    if (input && input.value !== String(multiplier)) {
      input.value = multiplier;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function watch(selector) {
    const formId = selector.getAttribute('data-form-id');

    if (!formId || selector.hasAttribute('data-pair-watched')) {
      return;
    }

    selector.setAttribute('data-pair-watched', '');
    multipliers[formId] = 1;

    selector.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) {
        return;
      }

      multipliers[formId] = parseInt(event.target.value, 10) || 1;
      const value = selector.querySelector('[data-pair-value]');
      value && (value.textContent = event.target.getAttribute('data-pair-label'));
      applyQuantity(formId);
      applyPrices(formId);
    });

    const observed = document.querySelectorAll(
      'product-meta[form-id="' + formId + '"] [data-product-price-list], product-sticky-form[form-id="' + formId + '"] .product-sticky-form__price'
    );
    observed.forEach((element) => new MutationObserver(() => applyPrices(formId)).observe(element, { childList: true, subtree: true, characterData: true }));
    applyPrices(formId);
  }

  const start = () => document.querySelectorAll('[data-pair-selector]').forEach(watch);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * "השלימי את הסט" — COMPLETE THE SET UNDER THE ADD TO CART BUTTON (store-specific, October 2026 A/B test)
 * ------------------------------------------------------------------------------------------------------------
 *
 * snippets/product-form.liquid renders the block (hidden) with the current product as a pre-checked row. This
 * code loads the product's complementary products (the Search & Discovery data the "Buy it with" block used)
 * from the recommendations endpoint, adds one checkbox row per product (first available variant), keeps the
 * current row in step with the chosen variant and quantity (so the single-or-pair selector is reflected), shows
 * the total of the checked rows and adds every checked row to the cart in one request, re-rendering and opening
 * the cart drawer exactly like Focal's product form does.
 *
 * A complementary product with options follows the current product's selected values (karat, gold colour) when
 * it has an available variant with those values; otherwise its first available variant is used. Each row names
 * the variant it will add.
 */
(function () {
  const routes = () => (window.themeVariables && window.themeVariables.routes) || {};
  const money = (cents) => window.hcMoney.formatMoney(cents);

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  }

  function imageUrl(src, width) {
    if (!src) {
      return '';
    }

    const url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  const normalize = (value) => String(value == null ? '' : value).trim().toLowerCase();

  function variantValues(variant) {
    return (Array.isArray(variant.options) ? variant.options : [variant.option1, variant.option2, variant.option3]).filter((value) => value != null && value !== '');
  }

  // The variant of a complementary product matching the current product's selected values, else its first available one
  function pickVariant(product, currentValues) {
    const available = product.variants.filter((variant) => variant.available);
    const candidates = available.length ? available : product.variants;
    const offered = new Set();
    product.variants.forEach((variant) => variantValues(variant).forEach((value) => offered.add(normalize(value))));
    const wanted = currentValues.map(normalize).filter((value) => offered.has(value));

    if (wanted.length) {
      const match = candidates.find((variant) => {
        const values = variantValues(variant).map(normalize);
        return wanted.every((value) => values.indexOf(value) !== -1);
      });

      if (match) {
        return match;
      }
    }

    return candidates[0];
  }

  function rowHtml(product, variant, id) {
    const image = product.featured_image ? '<img class="hc-set__image" loading="lazy" width="72" height="90" src="' + escapeHtml(imageUrl(product.featured_image, 216)) + '" alt="">' : '';
    const meta = product.variants.length > 1 ? '<span class="hc-set__meta text--subdued text--xsmall" data-set-row-meta>' + escapeHtml(variant.title) + '</span>' : '';

    return '<div class="checkbox-container hc-set__item" data-set-row>' +
      '<input type="checkbox" class="checkbox" id="' + id + '" data-set-checkbox data-variant-id="' + variant.id + '" data-price="' + variant.price + '">' +
      '<label for="' + id + '" class="hc-set__label">' + image +
        '<span class="hc-set__info"><span class="hc-set__name">' + escapeHtml(product.title) + '</span>' + meta +
          '<span class="hc-set__price" data-set-row-price>' + money(variant.price) + '</span></span>' +
      '</label>' +
    '</div>';
  }

  function updateRows(block, products, currentValues) {
    block.querySelectorAll('[data-set-row]').forEach((row, index) => {
      const product = products[index];
      const variant = product && pickVariant(product, currentValues);

      if (!variant) {
        return;
      }

      const checkbox = row.querySelector('[data-set-checkbox]');
      checkbox.setAttribute('data-variant-id', variant.id);
      checkbox.setAttribute('data-price', variant.price);
      row.querySelector('[data-set-row-price]').textContent = money(variant.price);
      const meta = row.querySelector('[data-set-row-meta]');
      meta && (meta.textContent = variant.title);
    });
  }

  function quantityOf(formId) {
    const input = document.querySelector('[form="' + formId + '"][name="quantity"]') || (document.getElementById(formId) || document).querySelector('[name="quantity"]');
    return Math.max(1, parseInt(input && input.value, 10) || 1);
  }

  function updateCurrent(block, formId) {
    const checkbox = block.querySelector('[data-set-current] [data-set-checkbox]');
    const form = document.getElementById(formId);
    const idInput = form && form.querySelector('[name="id"]');
    const quantity = quantityOf(formId);
    const price = parseInt(checkbox.getAttribute('data-price'), 10) || 0;

    idInput && checkbox.setAttribute('data-variant-id', idInput.value);
    checkbox.setAttribute('data-quantity', quantity);
    block.querySelector('[data-set-line-price]').textContent = money(price * quantity);

    const meta = block.querySelector('[data-set-current-meta]');
    const variantTitle = meta.getAttribute('data-variant-title') !== null ? meta.getAttribute('data-variant-title') : meta.textContent.trim();
    meta.setAttribute('data-variant-title', variantTitle);
    meta.textContent = [variantTitle, quantity > 1 ? '× ' + quantity : ''].filter(Boolean).join(' · ');
  }

  function updateTotal(block) {
    let total = 0;

    block.querySelectorAll('[data-set-checkbox]:checked').forEach((checkbox) => {
      total += (parseInt(checkbox.getAttribute('data-price'), 10) || 0) * (parseInt(checkbox.getAttribute('data-quantity'), 10) || 1);
    });

    block.querySelector('[data-set-total]').textContent = money(total);
    block.querySelector('[data-set-add]').disabled = total === 0;
  }

  async function addSet(block) {
    const button = block.querySelector('[data-set-add]');
    const items = Array.from(block.querySelectorAll('[data-set-checkbox]:checked')).map((checkbox) => ({
      id: parseInt(checkbox.getAttribute('data-variant-id'), 10),
      quantity: parseInt(checkbox.getAttribute('data-quantity'), 10) || 1
    }));

    if (!items.length || button.disabled) {
      return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch((routes().cartAddUrl || '/cart/add') + '.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items, sections: 'mini-cart' })
      });
      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'add failed');
      }

      // Same sequence as Focal's product form after a successful add
      const cartContent = await (await fetch((routes().cartUrl || '/cart') + '.js', { credentials: 'same-origin' })).json();
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } }));
      cartContent['sections'] = responseJson['sections'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartContent, openMiniCart: window.themeVariables.settings.cartType === 'drawer' }
      }));
    } catch (error) {
      console.error('[complete the set]', error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async function init(block) {
    if (block.hasAttribute('data-set-ready')) {
      return;
    }

    block.setAttribute('data-set-ready', '');
    const formId = block.getAttribute('data-form-id');
    const productId = block.getAttribute('data-product-id');

    let products = [];

    try {
      const response = await fetch((routes().productRecommendationsUrl || '/recommendations/products') + '.json?product_id=' + productId + '&intent=complementary&limit=4', { credentials: 'same-origin' });
      products = ((await response.json()).products || []).filter((product) => String(product.id) !== productId && product.available);
    } catch (error) {
      products = [];
    }

    const currentCheckbox = block.querySelector('[data-set-current] [data-set-checkbox]');
    let currentValues = (currentCheckbox.getAttribute('data-option-values') || '').split('|||').filter(Boolean);
    products = products.filter((product) => product.variants && product.variants.length);

    const rows = products.map((product, index) => rowHtml(product, pickVariant(product, currentValues), 'hc-set-' + productId + '-' + index)).join('');

    if (!rows) {
      return;
    }

    block.querySelector('[data-set-items]').insertAdjacentHTML('beforeend', rows);
    block.hidden = false;

    const refresh = () => { updateCurrent(block, formId); updateRows(block, products, currentValues); updateTotal(block); };
    const form = document.getElementById(formId);

    form && form.addEventListener('variant:changed', (event) => {
      const variant = event.detail && event.detail.variant;
      if (variant) {
        currentCheckbox.setAttribute('data-price', variant.price);
        block.querySelector('[data-set-current-meta]').setAttribute('data-variant-title', variant.title || '');
        currentValues = variantValues(variant);
      }
      refresh();
    });
    document.addEventListener('change', (event) => {
      if (event.target.name === 'quantity' && (event.target.getAttribute('form') === formId || (form && form.contains(event.target)))) {
        refresh();
      }
    });
    block.addEventListener('change', (event) => event.target.matches('[data-set-checkbox]') && updateTotal(block));
    block.querySelector('[data-set-add]').addEventListener('click', () => addSet(block));
    refresh();
  }

  const start = () => document.querySelectorAll('[data-set]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);
})();


/* Dynamic checkout button: show "הזמיני עכשיו" instead of Shopify's own "קנה את זה עכשיו" (Oct 2026 A/B test).
   Shopify renders and re-renders that button client-side, so the label is kept in place by a MutationObserver. */
(function () {
  const LABEL = 'הזמיני עכשיו';
  const SELECTOR = '.shopify-payment-button__button--unbranded';

  function relabel(root) {
    root.querySelectorAll(SELECTOR).forEach((button) => {
      if (button.textContent.trim() !== LABEL) button.textContent = LABEL;
    });
  }

  function watch(container) {
    if (container.hasAttribute('data-buy-now-label-watched')) return;
    container.setAttribute('data-buy-now-label-watched', '');
    relabel(container);
    new MutationObserver(() => relabel(container)).observe(container, { childList: true, subtree: true, characterData: true });
  }

  const start = () => document.querySelectorAll('.shopify-payment-button').forEach(watch);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);
})();
